import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { getApiErrorMessage } from '@/lib/utils/error.utils';
import { PARTNER_PROFILE_STRINGS } from './partner-profile.strings';
import {
  deletePartnerProfile,
  savePartnerProfile,
  type PartnerProfilePayload,
} from '../../api/partner-profile.api';

/** Matches `partnerProfileModule.queryKey` (kept local to avoid a circular import back to `../partner`). */
const partnerProfileQueryKey = (entityId: string) => ['profile', 'partner', entityId];

export interface SavePartnerProfileOptions {
  /** Runs after the cache is updated - the section panel closes and toasts here. */
  onSaved?: () => void;
}

/** Saves the partner profile (FR-28) and refreshes the cache. */
export function useSavePartnerProfile(entityId: string, options: SavePartnerProfileOptions = {}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: PartnerProfilePayload) => savePartnerProfile(entityId, payload),
    onSuccess: (data) => {
      queryClient.setQueryData(partnerProfileQueryKey(entityId), data);
      options.onSaved?.();
    },
  });
}

/** Deletes the partner profile and clears the cached copy. */
export function useDeletePartnerProfile(entityId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => deletePartnerProfile(entityId),
    onSuccess: () => {
      queryClient.setQueryData(partnerProfileQueryKey(entityId), {});
      toast.success(PARTNER_PROFILE_STRINGS.DELETE_SUCCESS);
    },
    // The page behind it has no open surface left, so the API's reason goes to a toast.
    onError: (error) => {
      toast.error(getApiErrorMessage(error, PARTNER_PROFILE_STRINGS.DELETE_ERROR));
    },
  });
}
